import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Plus, Trash2, CheckCircle2, X, ChevronsUpDown, Check, User, MapPin, Phone, FileText as FileTextIcon, Send, Home } from "lucide-react";
import { toast } from "sonner";
import { LineImageThumb } from "@/components/LineImageThumb";
import { TermsTemplatePicker } from "@/components/TermsTemplatePicker";
import { useActiveTermsTemplates } from "@/hooks/useActiveTermsTemplates";
import { buildTermsSnapshot, splitTermsSnapshot } from "@/lib/termsSnapshot";
import { CurrencySelect } from "@/components/CurrencySelect";
import { formatMoney, docCurrency, currencySymbol } from "@/lib/currency";

const UOMS = ["PCS", "KG", "MTR", "SET", "BOX", "NOS", "LTR"];

const emptyLine = (defaultGstPct = 18, gstType = "intra") => {
  const g = Number(defaultGstPct || 0);
  const half = Math.round((g / 2) * 100) / 100;
  const split = gstType === "inter"
    ? { cgst_pct: 0, sgst_pct: 0, igst_pct: g }
    : { cgst_pct: half, sgst_pct: half, igst_pct: 0 };
  return {
    item_id: "", qty: 1, unit_price: 0,
    hsn_code: "", uom: "PCS",
    discount_pct: 0, ...split,
    tax_pct: g,
    delivery_date: "",
  };
};

const classifyGst = (custState, companyState) => {
  const a = (custState || "").trim().toLowerCase();
  const b = (companyState || "").trim().toLowerCase();
  if (!a || !b) return "unknown";
  return a === b ? "intra" : "inter";
};

const splitGstPct = (gstType, totalPct) => {
  const g = Number(totalPct || 0);
  if (gstType === "inter") return { cgst_pct: 0, sgst_pct: 0, igst_pct: g, tax_pct: g };
  const half = Math.round((g / 2) * 100) / 100;
  return { cgst_pct: half, sgst_pct: half, igst_pct: 0, tax_pct: g };
};

const lineAmount = (l) => {
  const gross = Number(l.qty || 0) * Number(l.unit_price || 0);
  const afterDisc = gross * (1 - Number(l.discount_pct || 0) / 100);
  const totalGst = (Number(l.cgst_pct || 0) + Number(l.sgst_pct || 0) + Number(l.igst_pct || 0)) / 100;
  return afterDisc * (1 + totalGst);
};

export default function RaiseQuotationPage() {
  const navigate = useNavigate();
  const { id: editId } = useParams(); // present in edit mode
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [gstRates, setGstRates] = useState([]);
  const [companyState, setCompanyState] = useState("");
  const [company, setCompany] = useState(null);
  const { templates: termsTemplates, loading: termsLoading } = useActiveTermsTemplates();
  const [busy, setBusy] = useState(false);
  const [custPickerOpen, setCustPickerOpen] = useState(false);
  const [existing, setExisting] = useState(null);
  const [form, setForm] = useState({
    customer_id: "",
    customer_name: "",
    customer_contact: "",
    customer_email: "",
    date: new Date().toISOString().slice(0, 10),
    validity_date: "",
    po_reference: "",
    status: "draft",
    notes: "",
    // Document currency — every price/total on this quote is in it (no conversion).
    currency: "INR",
    header_discount: 0,
    additional_charges: 0,
    additional_charges_label: "Freight / Other",
    terms_ids: [],
    // Per-template inline edits (keyed by terms.id), an override for the
    // "no template selected → company default" text, and free-form extra
    // text layered on top of the selection. All local-only until submit,
    // when they're captured into `terms_snapshot` on the quote.
    terms_overrides: {},
    terms_default_override: undefined,
    terms_extra_text: "",
    lines: [emptyLine()],
  });

  const fgItems = useMemo(() => items.filter((i) => i.type === "FG"), [items]);
  const customer = useMemo(() => customers.find((c) => c.id === form.customer_id), [customers, form.customer_id]);
  const gstType = classifyGst(customer?.billing_state || customer?.state, companyState);
  const activeGstRates = useMemo(() => gstRates.filter((r) => r.status !== "inactive"), [gstRates]);
  const defaultGstPct = useMemo(() => {
    const d = activeGstRates.find((r) => r.is_default);
    if (d) return Number(d.gst_pct);
    if (activeGstRates.length > 0) return Number(activeGstRates[0].gst_pct);
    return 18;
  }, [activeGstRates]);

  const fetchGstRates = async () => {
    try {
      const g = await api.get("/gst-rates");
      setGstRates(g.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      const [c, i, cs] = await Promise.all([
        api.get("/customers"),
        api.get("/items"),
        api.get("/settings/company").catch(() => ({ data: { state: "" } })),
      ]);
      setCustomers(c.data);
      setItems(i.data);
      setCompany(cs.data || null);
      setCompanyState(cs.data?.state || "");
      await fetchGstRates();

      // Edit mode: load quotation
      if (editId) {
        try {
          const { data: q } = await api.get(`/quotations/${editId}`);
          setExisting(q);
          // Rehydrate any previously-saved inline T&C edits (per-template
          // overrides, the edited company-default text, and any one-off
          // extra text) so re-opening the draft shows them again.
          const { overrides: termsOverrides, defaultOverride: termsDefaultOverride, extraText: termsExtraText } = splitTermsSnapshot(q.terms_snapshot);
          setForm((f) => ({
            ...f,
            customer_id: q.customer_id || "",
            customer_name: q.customer_name || "",
            customer_contact: q.customer_contact || "",
            customer_email: q.customer_email || "",
            date: q.date ? String(q.date).slice(0, 10) : f.date,
            validity_date: q.validity_date ? String(q.validity_date).slice(0, 10) : "",
            po_reference: q.po_reference || "",
            status: q.status || "draft",
            notes: q.notes || "",
            currency: docCurrency(q),
            terms_ids: Array.isArray(q.terms_ids) && q.terms_ids.length ? q.terms_ids : (q.terms_id ? [q.terms_id] : []),
            terms_overrides: termsOverrides,
            terms_default_override: termsDefaultOverride,
            terms_extra_text: termsExtraText,
            header_discount: q.header_discount || 0,
            additional_charges: q.additional_charges || 0,
            additional_charges_label: q.additional_charges_label || "Freight / Other",
            lines: (q.lines || []).length ? q.lines.map((l) => {
              const splitSum = Number(l.cgst_pct || 0) + Number(l.sgst_pct || 0) + Number(l.igst_pct || 0);
              const fallbackGst = splitSum > 0 ? 0 : Number(l.tax_pct || 0);
              // If legacy line has only tax_pct set, split it now so a subsequent save preserves GST.
              const derived = fallbackGst > 0
                ? { cgst_pct: Math.round((fallbackGst / 2) * 100) / 100, sgst_pct: Math.round((fallbackGst / 2) * 100) / 100, igst_pct: 0 }
                : { cgst_pct: l.cgst_pct || 0, sgst_pct: l.sgst_pct || 0, igst_pct: l.igst_pct || 0 };
              return {
                item_id: l.item_id,
                qty: l.qty,
                unit_price: l.unit_price,
                hsn_code: l.hsn_code || "",
                uom: l.uom || "PCS",
                discount_pct: l.discount_pct || 0,
                ...derived,
                tax_pct: derived.cgst_pct + derived.sgst_pct + derived.igst_pct,
                delivery_date: l.delivery_date ? String(l.delivery_date).slice(0, 10) : "",
              };
            }) : [emptyLine()],
          }));
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || "Could not load quotation");
        }
      }
    })();
  }, []);

  useEffect(() => {
    const onUpdate = () => fetchGstRates();
    window.addEventListener("gst-rates-updated", onUpdate);
    window.addEventListener("focus", onUpdate);
    return () => {
      window.removeEventListener("gst-rates-updated", onUpdate);
      window.removeEventListener("focus", onUpdate);
    };
  }, []);

  // Auto-fill default GST on pristine (item-less) lines when defaults change
  useEffect(() => {
    setForm((f) => ({
      ...f,
      lines: f.lines.map((l) => {
        if (l.item_id) return l;
        return { ...l, ...splitGstPct(gstType === "inter" ? "inter" : "intra", defaultGstPct) };
      }),
    }));
  }, [defaultGstPct, gstType]);

  const setLine = (idx, patch) => {
    setForm((f) => ({ ...f, lines: f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));
  };
  const setLineWithItem = (idx, itemId) => {
    const it = items.find((x) => x.id === itemId);
    if (!it) return setLine(idx, { item_id: itemId });
    setLine(idx, {
      item_id: itemId,
      // The Items master rate/cost is INR — only pre-fill it on an INR quote.
      unit_price: form.currency === "INR" ? (it.rate || it.cost || 0) : 0,
      hsn_code: it.hsn_code || "",
      uom: it.uom || "PCS",
    });
  };
  const setLineGstTotal = (idx, totalPct) => {
    setLine(idx, splitGstPct(gstType === "inter" ? "inter" : "intra", totalPct));
  };
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine(defaultGstPct, gstType === "inter" ? "inter" : "intra")] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  // Totals — identical maths to Transfer Order
  const subtotal   = form.lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unit_price || 0), 0);
  const totalDisc  = form.lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unit_price || 0) * Number(l.discount_pct || 0) / 100, 0);
  const netTaxable = subtotal - totalDisc;
  const headerDiscAmt = netTaxable * (Number(form.header_discount || 0) / 100);
  const afterHeaderDisc = netTaxable - headerDiscAmt;
  const totalGst = form.lines.reduce((s, l) => {
    const g = Number(l.qty || 0) * Number(l.unit_price || 0) * (1 - Number(l.discount_pct || 0) / 100) * (1 - Number(form.header_discount || 0) / 100);
    return s + g * (Number(l.cgst_pct || 0) + Number(l.sgst_pct || 0) + Number(l.igst_pct || 0)) / 100;
  }, 0);
  const grandTotal = afterHeaderDisc + totalGst + Number(form.additional_charges || 0);

  const cancel = () => navigate("/quotations");

  const submit = async () => {
    if (!form.customer_name && !customer) return toast.error("Please select a customer");
    const validLines = form.lines.filter((l) => l.item_id && Number(l.qty) > 0);
    if (validLines.length === 0) return toast.error("Please add at least one item");

    setBusy(true);
    try {
      const payload = {
        customer_id: form.customer_id || null,
        customer_name: form.customer_name || (customer?.name || ""),
        customer_contact: form.customer_contact || customer?.phone || "",
        customer_email: form.customer_email || customer?.email || "",
        date: form.date ? new Date(form.date).toISOString() : new Date().toISOString(),
        validity_date: form.validity_date ? new Date(form.validity_date).toISOString() : null,
        po_reference: form.po_reference,
        status: form.status,
        notes: form.notes,
        currency: form.currency,
        gst_pct: defaultGstPct,
        header_discount: Number(form.header_discount || 0),
        additional_charges: Number(form.additional_charges || 0),
        additional_charges_label: form.additional_charges_label,
        terms_ids: form.terms_ids || [],
        terms_snapshot: buildTermsSnapshot({
          templates: termsTemplates,
          selectedIds: form.terms_ids,
          overrides: form.terms_overrides,
          defaultText: company?.default_terms,
          defaultOverride: form.terms_default_override,
          extraText: form.terms_extra_text,
        }),
        lines: validLines.map((l) => ({
          item_id: l.item_id,
          qty: Number(l.qty),
          unit_price: Number(l.unit_price),
          hsn_code: l.hsn_code || "",
          uom: l.uom || "PCS",
          discount_pct: Number(l.discount_pct || 0),
          cgst_pct: Number(l.cgst_pct || 0),
          sgst_pct: Number(l.sgst_pct || 0),
          igst_pct: Number(l.igst_pct || 0),
          tax_pct: Number(l.cgst_pct || 0) + Number(l.sgst_pct || 0) + Number(l.igst_pct || 0),
          delivery_date: l.delivery_date ? new Date(l.delivery_date).toISOString() : null,
        })),
      };
      if (editId) {
        await api.patch(`/quotations/${editId}`, payload);
        toast.success(`${existing?.code || "Quotation"} updated`);
      } else {
        const { data } = await api.post("/quotations", payload);
        toast.success(`${data.code} created`);
      }
      navigate("/quotations");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save quotation");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="raise-quotation-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Sales</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            {editId ? `Edit Quotation${existing ? " · " + existing.code : ""}` : "New Quotation"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Prepare a customer quotation for Finished Goods. Convert to a
            <span className="mono text-blue-700"> Transfer Order</span> once approved.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate("/quotations")} data-testid="view-all-quotations-btn">
          <FileTextIcon className="w-4 h-4 mr-1.5" /> View All Quotations
        </Button>
      </div>

      {/* Customer + top form */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-6">
            <Label>Customer *</Label>
            <Popover open={custPickerOpen} onOpenChange={setCustPickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-between mt-1" data-testid="customer-picker">
                  {customer ? `${customer.code} — ${customer.name}` : (form.customer_name || "Select customer…")}
                  <ChevronsUpDown className="w-4 h-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[420px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search by name, code, GSTIN…" />
                  <CommandList>
                    <CommandEmpty>No customers found.</CommandEmpty>
                    <CommandGroup>
                      {customers.map((c) => (
                        <CommandItem
                          key={c.id}
                          value={`${c.code} ${c.name} ${c.gstin || ""}`}
                          onSelect={() => {
                            setForm((f) => ({
                              ...f,
                              customer_id: c.id,
                              customer_name: c.name || f.customer_name,
                              customer_contact: c.phone || f.customer_contact,
                              customer_email: c.email || f.customer_email,
                            }));
                            setCustPickerOpen(false);
                          }}
                          data-testid={`customer-option-${c.code}`}
                        >
                          <Check className={`w-4 h-4 mr-2 ${form.customer_id === c.id ? "opacity-100" : "opacity-0"}`} />
                          <div>
                            <div className="text-sm font-medium">{c.name}</div>
                            <div className="text-[11px] text-slate-500 mono">{c.code} · {c.gstin || "No GSTIN"}</div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {customer && (
              <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]" data-testid="customer-summary">
                <div className="bg-slate-50 border border-slate-100 rounded p-2">
                  <div className="flex items-center gap-1 font-semibold text-slate-700"><User className="w-3 h-3" /> Contact</div>
                  <div className="text-slate-600">{customer.contact_person || "—"}</div>
                  <div className="text-slate-600 flex items-center gap-1"><Phone className="w-3 h-3" /> {customer.phone || "—"}</div>
                </div>
                <div className="bg-slate-50 border border-slate-100 rounded p-2">
                  <div className="flex items-center gap-1 font-semibold text-slate-700"><MapPin className="w-3 h-3" /> Billing</div>
                  <div className="text-slate-600 whitespace-pre-line">{customer.billing_line1 || customer.address || "—"}</div>
                </div>
              </div>
            )}
          </div>

          <div className="md:col-span-3">
            <Label>Quotation Date</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" data-testid="quo-date-input" />
          </div>
          <div className="md:col-span-3">
            <Label>Valid Till</Label>
            <Input type="date" value={form.validity_date} onChange={(e) => setForm({ ...form, validity_date: e.target.value })} className="mt-1" data-testid="quo-validity-input" />
          </div>

          <div className="md:col-span-6">
            <Label>Customer Name {customer ? "" : "*"}</Label>
            <Input
              value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              placeholder="Full customer name (auto-filled from picker)"
              className="mt-1"
              data-testid="quo-customer-name-input"
            />
          </div>

          <div className="md:col-span-3">
            <Label>Contact</Label>
            <Input
              value={form.customer_contact}
              onChange={(e) => setForm({ ...form, customer_contact: e.target.value })}
              placeholder="Phone"
              className="mt-1"
              data-testid="quo-contact-input"
            />
          </div>

          <div className="md:col-span-3">
            <Label>Email</Label>
            <Input
              type="email"
              value={form.customer_email}
              onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
              placeholder="name@customer.com"
              className="mt-1"
              data-testid="quo-email-input"
            />
          </div>

          <div className="md:col-span-4">
            <Label>PO Reference</Label>
            <Input
              value={form.po_reference}
              onChange={(e) => setForm({ ...form, po_reference: e.target.value })}
              placeholder="Buyer's PO number (optional)"
              className="mt-1"
              data-testid="quo-po-reference-input"
            />
          </div>

          <CurrencySelect
            className="md:col-span-4"
            value={form.currency}
            onChange={(v) => setForm((f) => ({ ...f, currency: v }))}
            hasAmounts={form.lines.some((l) => Number(l.unit_price) > 0)}
            testid="quo-currency"
          />

          <div className="md:col-span-4">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="mt-1" data-testid="quo-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-12">
            <Label>Notes</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Terms, packaging, freight preferences, warranty…"
              className="mt-1"
              data-testid="quo-notes-input"
            />
          </div>
        </div>

        {customer && gstType !== "unknown" && (
          <div className="mt-3 text-[11px] uppercase tracking-[0.14em] font-semibold px-2 py-1 inline-block rounded-full"
               style={{ backgroundColor: gstType === "intra" ? "#dbeafe" : "#fef3c7", color: gstType === "intra" ? "#1e40af" : "#92400e" }}>
            GST Treatment: {gstType === "intra" ? "Intra-State (CGST + SGST)" : "Inter-State (IGST)"}
          </div>
        )}
      </div>

      {/* Line-items table */}
      <div className="bg-white border-y border-slate-200 overflow-hidden -mx-6 md:-mx-8">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Quotation Items</h3>
          <p className="text-xs text-slate-500 mt-0.5">Finished Goods only — line-level GST, discount and delivery date.</p>
        </div>
        <div className="overflow-x-auto" data-testid="quo-lines-scroll">
          <table className="text-sm w-full table-fixed" style={{ minWidth: "1280px" }}>
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 bg-slate-50 whitespace-nowrap">
                <th className="py-2 px-3" style={{ width: "3%" }}>#</th>
                <th className="py-2 px-3" style={{ width: "5%" }}>Image</th>
                <th className="py-2 px-3" style={{ width: "26%" }}>Item *</th>
                <th className="py-2 px-3" style={{ width: "12%" }}>Part No</th>
                <th className="py-2 px-3" style={{ width: "7%" }}>Qty *</th>
                <th className="py-2 px-3" style={{ width: "6%" }}>UOM</th>
                <th className="py-2 px-3" style={{ width: "8%" }}>Rate ({currencySymbol(form.currency)}) *</th>
                <th className="py-2 px-3" style={{ width: "7%" }}>HSN</th>
                <th className="py-2 px-3" style={{ width: "8%" }}>GST %</th>
                <th className="py-2 px-3" style={{ width: "5%" }}>Disc %</th>
                <th className="py-2 px-3 text-right" style={{ width: "9%" }}>Amount</th>
                <th className="py-2 px-3" style={{ width: "4%" }}></th>
              </tr>
            </thead>
            <tbody>
              {form.lines.map((l, idx) => {
                const totalGstPct = Number(l.cgst_pct || 0) + Number(l.sgst_pct || 0) + Number(l.igst_pct || 0);
                const selectedItem = fgItems.find((it) => it.id === l.item_id);
                return (
                  <tr key={idx} className="border-t border-slate-100" data-testid={`line-row-${idx}`}>
                    <td className="py-2 px-3 text-xs text-slate-500 mono">{idx + 1}</td>
                    <td className="py-2 px-3">
                      <LineImageThumb item={selectedItem} testid={`line-image-${idx}`} />
                    </td>
                    <td className="py-2 px-3">
                      <LineItemPicker
                        value={l.item_id}
                        selected={selectedItem}
                        items={fgItems}
                        onPick={(id) => setLineWithItem(idx, id)}
                        testId={`line-item-${idx}`}
                      />
                    </td>
                    <td className="py-2 px-3 mono text-slate-700 break-all" data-testid={`line-part-no-${idx}`}>
                      {selectedItem?.code || "—"}
                    </td>
                    <td className="py-2 px-3">
                      <Input type="number" value={l.qty} onChange={(e) => setLine(idx, { qty: e.target.value })} data-testid={`line-qty-${idx}`} />
                    </td>
                    <td className="py-2 px-3">
                      <Select value={l.uom} onValueChange={(v) => setLine(idx, { uom: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {UOMS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-2 px-3">
                      <Input type="number" value={l.unit_price} onChange={(e) => setLine(idx, { unit_price: e.target.value })} data-testid={`line-price-${idx}`} />
                    </td>
                    <td className="py-2 px-3">
                      <Input value={l.hsn_code} onChange={(e) => setLine(idx, { hsn_code: e.target.value })} data-testid={`line-hsn-${idx}`} />
                    </td>
                    <td className="py-2 px-3">
                      {activeGstRates.length > 0 ? (
                        <Select value={String(totalGstPct)} onValueChange={(v) => setLineGstTotal(idx, Number(v))}>
                          <SelectTrigger data-testid={`line-gst-${idx}`}><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            {activeGstRates.map((r) => (
                              <SelectItem key={r.id} value={String(r.gst_pct)} data-testid={`line-gst-${idx}-opt-${r.gst_pct}`}>
                                {r.gst_pct}%{r.is_default ? " (Default)" : ""}
                              </SelectItem>
                            ))}
                            <SelectItem value="0">0%</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input type="number" value={totalGstPct} onChange={(e) => setLineGstTotal(idx, Number(e.target.value))} data-testid={`line-gst-input-${idx}`} />
                      )}
                    </td>
                    <td className="py-2 px-3">
                      <Input type="number" value={l.discount_pct} onChange={(e) => setLine(idx, { discount_pct: e.target.value })} data-testid={`line-disc-${idx}`} />
                    </td>
                    <td className="py-2 px-3 mono text-right font-semibold text-slate-800 whitespace-nowrap">
                      {formatMoney(lineAmount(l), form.currency)}
                    </td>
                    <td className="py-2 px-3">
                      <Button variant="ghost" size="icon" onClick={() => removeLine(idx)} disabled={form.lines.length === 1} data-testid={`line-delete-${idx}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Add Item sits below the table it feeds, not above it */}
        <div className="px-5 py-3 border-t border-slate-100">
          <Button onClick={addLine} className="bg-blue-600 hover:bg-blue-700" data-testid="add-line-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Item
          </Button>
        </div>

        {/* Totals only — Header Discount + Additional Charges removed per Phase 34 */}
        <div className="px-5 py-4 border-t border-slate-100 flex justify-end">
          <div className="w-full sm:w-96 text-sm space-y-1.5 bg-slate-50 border border-slate-100 rounded-md p-4" data-testid="totals-panel">
            <Row label="Total">{formatMoney(subtotal, form.currency)}</Row>
            <Row label="Line Discount">− {formatMoney(totalDisc, form.currency)}</Row>
            <Row label="Total GST">+ {formatMoney(totalGst, form.currency)}</Row>
            <div className="pt-2 border-t border-slate-200 flex items-center justify-between font-extrabold text-slate-900 text-base">
              <span>Grand Total</span>
              <span className="mono" data-testid="grand-total">{formatMoney(grandTotal, form.currency, { withCode: true })}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Phase 34.2 — Terms & Bank on the form */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <TermsTemplatePicker
            templates={termsTemplates}
            loading={termsLoading}
            selectedIds={form.terms_ids}
            onSelectedIdsChange={(ids) => setForm((f) => ({ ...f, terms_ids: ids }))}
            overrides={form.terms_overrides}
            onOverridesChange={(overrides) => setForm((f) => ({ ...f, terms_overrides: overrides }))}
            defaultText={company?.default_terms}
            defaultOverride={form.terms_default_override}
            onDefaultOverrideChange={(v) => setForm((f) => ({ ...f, terms_default_override: v }))}
            extraText={form.terms_extra_text}
            onExtraTextChange={(v) => setForm((f) => ({ ...f, terms_extra_text: v }))}
            docLabel="quotation"
          />
        </div>

        <div>
          <Label>Bank Details <span className="text-[10px] font-normal text-slate-500">(from Settings — read only)</span></Label>
          <div className="mt-1 rounded-md border border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-700" data-testid="bank-preview">
            {company && (company.default_bank_name || company.default_bank_account_number) ? (
              <table className="text-[12px]"><tbody>
                {company.default_bank_name && <tr><td className="pr-3 font-semibold text-slate-500 py-0.5">Bank</td><td>{company.default_bank_name}</td></tr>}
                {company.default_bank_account_name && <tr><td className="pr-3 font-semibold text-slate-500 py-0.5">Holder</td><td>{company.default_bank_account_name}</td></tr>}
                {company.default_bank_account_number && <tr><td className="pr-3 font-semibold text-slate-500 py-0.5">A/c No</td><td className="mono">{company.default_bank_account_number}</td></tr>}
                {company.default_bank_ifsc && <tr><td className="pr-3 font-semibold text-slate-500 py-0.5">IFSC</td><td className="mono">{company.default_bank_ifsc}</td></tr>}
                {company.default_bank_branch && <tr><td className="pr-3 font-semibold text-slate-500 py-0.5">Branch</td><td>{company.default_bank_branch}</td></tr>}
                {company.default_bank_upi_id && <tr><td className="pr-3 font-semibold text-slate-500 py-0.5">UPI</td><td className="mono">{company.default_bank_upi_id}</td></tr>}
              </tbody></table>
            ) : <span className="text-slate-400 italic">No bank details configured. Add them in Settings → Company Profile.</span>}
          </div>
        </div>
      </div>

      {/* Sticky action bar */}
      <div className="sticky bottom-0 -mx-4 sm:-mx-8 lg:-mx-10 px-4 sm:px-8 lg:px-10 py-3 bg-white border-t border-slate-200 flex items-center justify-between shadow-lg z-10">
        <div className="text-xs text-slate-500">
          {form.lines.filter((l) => l.item_id && l.qty > 0).length} item(s) · Grand total <span className="mono font-semibold text-slate-800">{formatMoney(grandTotal, form.currency, { withCode: true })}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={cancel} disabled={busy} data-testid="cancel-btn">
            <Home className="w-4 h-4 mr-1.5" /> Cancel
          </Button>
          <Button onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-quotation-btn">
            {busy ? <><CheckCircle2 className="w-4 h-4 mr-1.5 animate-pulse" /> {editId ? "Saving…" : "Creating…"}</> : <><Send className="w-4 h-4 mr-1.5" /> {editId ? "Save Changes" : "Create Quotation"}</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function LineItemPicker({ value, selected, items, onPick, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between h-9 font-normal" data-testid={testId}>
          <span className="truncate text-left">{selected ? `${selected.code} — ${selected.name}` : "Pick FG part"}</span>
          <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[380px]" align="start">
        <Command>
          <CommandInput placeholder="Search by part number or name…" data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>No items found</CommandEmpty>
            <CommandGroup>
              {items.map((it) => (
                <CommandItem
                  key={it.id}
                  value={`${it.code} ${it.name}`}
                  onSelect={() => { onPick(it.id); setOpen(false); }}
                  data-testid={`${testId}-opt-${it.code}`}
                >
                  <Check className={`w-3.5 h-3.5 mr-2 ${value === it.id ? "opacity-100" : "opacity-0"}`} />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{it.code}</span>
                    <span className="text-[11px] text-slate-500 truncate">{it.name}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between text-slate-700">
      <span className="text-xs uppercase tracking-[0.1em] text-slate-500">{label}</span>
      <span className="mono">{children}</span>
    </div>
  );
}
